/**
 * "This just changed" highlight for <g-table> cells — the pure half.
 *
 * A cell flashes when a data update (a live `source` broadcast, a poll that
 * re-assigns `.data`) brings a different value for a record the table already
 * had. Changes are found by comparing values per record `_id` between updates,
 * not per render, so sorting, paging and searching never flash anything, and a
 * row seen for the first time (initial load, a created record) doesn't either.
 * Rows without an `_id` can't be followed across updates and never flash.
 */

/** How long the fade runs. The CSS animation in util/status.js matches it. */
export const FLASH_MS = 1600;

/** A comparable stand-in for a cell value; objects (refs, arrays) by content. */
export function signature(v) {
  if (v == null) return '';
  if (typeof v !== 'object') return `${typeof v}:${v}`;
  try { return `o:${JSON.stringify(v)}`; } catch { return 'o:?'; }
}

/** id → Map(field → signature), for the rows that have an `_id`. */
export function snapshotRows(rows, fields, read) {
  const snap = new Map();
  for (const row of rows) {
    if (!row || row._id == null) continue;
    const vals = new Map();
    for (const f of fields) vals.set(f, signature(read(row, f)));
    snap.set(String(row._id), vals);
  }
  return snap;
}

/** Cell keys (`flashKey(id, field)`) whose value differs between two snapshots. */
export function changedCells(prev, next) {
  const out = [];
  if (!prev) return out;
  for (const [id, vals] of next) {
    const before = prev.get(id);
    if (!before) continue;
    for (const [f, sig] of vals) {
      if (before.has(f) && before.get(f) !== sig) out.push(flashKey(id, f));
    }
  }
  return out;
}

export const flashKey = (id, field) => `${id}\u0000${field}`;
